import { useState, useEffect, useMemo, useRef } from 'react'
import {
  Loader2,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  Users,
  Building2,
  UserPlus,
  Link2,
  KeyRound,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserDetailsPanel } from './UserDetailsPanel'
import { OrganizationAdminView } from './OrganizationAdminView'
import {
  useIsAdmin,
  useAdminUsers,
  useInviteUserWithTrial,
  useGenerateInviteLink,
  useGenerateResetLink,
  useUserLicenses,
  useUserDevices,
  useUserMaps,
  useUpdateUserLicense,
  useCreateUserLicense,
  useDeleteDeviceLicense,
  useToggleAdminStatus,
  useToggleBetaTesterStatus,
  formatLastSeen,
  type EnrichedUser,
} from '@/hooks/useAdmin'

type SortColumn = 'name' | 'email' | 'last_sign_in' | 'devices' | 'quota' | 'role' | 'beta'
type SortDirection = 'asc' | 'desc'
type FilterOption = 'all' | 'admins' | 'beta' | 'licensed' | 'unlicensed'

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number]

const FILTER_OPTIONS: { value: FilterOption; label: string }[] = [
  { value: 'all', label: 'All Users' },
  { value: 'admins', label: 'Admins Only' },
  { value: 'beta', label: 'Beta Only' },
  { value: 'licensed', label: 'With License' },
  { value: 'unlicensed', label: 'Without License' },
]

export function AdminPage() {
  const [adminView, setAdminView] = useState<'users' | 'organizations'>('users')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [panelState, setPanelState] = useState<'closed' | 'open' | 'closing'>('closed')
  const [sortColumn, setSortColumn] = useState<SortColumn>('last_sign_in')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)
  const [filterOption, setFilterOption] = useState<FilterOption>('all')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteMessage, setInviteMessage] = useState<string | null>(null)
  const [generatedLink, setGeneratedLink] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Admin check
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()

  // Fetch data
  const { data: users = [], isLoading: usersLoading, isError: usersError } = useAdminUsers()
  const { data: licenses = [], isLoading: licensesLoading } = useUserLicenses(selectedUserId)
  const { data: devices = [], isLoading: devicesLoading } = useUserDevices(selectedUserId)
  const { data: maps = [], isLoading: mapsLoading } = useUserMaps(selectedUserId)

  // Mutations
  const updateLicense = useUpdateUserLicense()
  const createLicense = useCreateUserLicense()
  const deleteDevice = useDeleteDeviceLicense()
  const toggleAdmin = useToggleAdminStatus()
  const toggleBetaTester = useToggleBetaTesterStatus()
  const inviteUser = useInviteUserWithTrial()
  const generateInviteLink = useGenerateInviteLink()
  const generateResetLink = useGenerateResetLink()
  // All three actions share one email field and one result row, so any in-flight
  // request disables the lot.
  const anyLinkPending = inviteUser.isPending || generateInviteLink.isPending || generateResetLink.isPending

  const selectedUser = users.find((u) => u.id === selectedUserId) ?? null

  // Handle column sort
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    // Apply search filter
    let result = searchQuery.trim()
      ? users.filter(
          (user) =>
            user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.id.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : [...users]

    // Apply category filter
    switch (filterOption) {
      case 'admins':
        result = result.filter((u) => u.is_admin)
        break
      case 'beta':
        result = result.filter((u) => u.is_beta_tester)
        break
      case 'licensed':
        result = result.filter((u) => u.active_license !== null)
        break
      case 'unlicensed':
        result = result.filter((u) => u.active_license === null)
        break
    }

    // Sort
    result.sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1

      switch (sortColumn) {
        case 'name':
          return dir * (a.full_name ?? '').localeCompare(b.full_name ?? '')
        case 'email':
          return dir * (a.email ?? '').localeCompare(b.email ?? '')
        case 'last_sign_in':
          return dir * (a.last_sign_in_at ?? '').localeCompare(b.last_sign_in_at ?? '')
        case 'devices':
          return dir * (a.device_count - b.device_count)
        case 'quota':
          return dir * (a.used_quota_km2 - b.used_quota_km2)
        case 'role':
          return dir * ((a.is_admin ? 1 : 0) - (b.is_admin ? 1 : 0))
        case 'beta':
          return dir * ((a.is_beta_tester ? 1 : 0) - (b.is_beta_tester ? 1 : 0))
        default:
          return 0
      }
    })

    return result
  }, [users, searchQuery, filterOption, sortColumn, sortDirection])

  // Pagination
  const totalUsers = filteredAndSortedUsers.length
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize))
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredAndSortedUsers.slice(start, start + pageSize)
  }, [filteredAndSortedUsers, currentPage, pageSize])

  // Reset to page 1 when search, filter, or page size changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, filterOption, pageSize])

  // Keyboard shortcut "/" to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Open panel when user is selected
  const handleSelectUser = (userId: string) => {
    if (selectedUserId === userId) {
      // Close if same user clicked
      closePanel()
    } else {
      setSelectedUserId(userId)
      setPanelState('open')
    }
  }

  // Close panel with animation
  const closePanel = () => {
    setPanelState('closing')
  }

  // Handle animation end
  useEffect(() => {
    if (panelState === 'closing') {
      const timer = setTimeout(() => {
        setPanelState('closed')
        setSelectedUserId(null)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [panelState])

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && panelState === 'open') {
        closePanel()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [panelState])

  // Loading state
  if (isAdminLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Access denied
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Access denied</p>
          <p className="text-xs text-muted-foreground/50 mt-1">You don't have permission to view this page</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-xl font-medium text-foreground tracking-tight">Admin</h1>
          <p className="text-sm text-muted-foreground/70 mt-1">Manage users, licenses, devices, and quotas</p>
        </div>

        <div className="flex gap-1 border-b border-border">
          <button
            onClick={() => setAdminView('users')}
            className={cn(
              'flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition-colors -mb-px',
              adminView === 'users'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Users className="h-4 w-4" />
            Users
          </button>
          <button
            onClick={() => {
              setAdminView('organizations')
              setPanelState('closed')
              setSelectedUserId(null)
            }}
            className={cn(
              'flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition-colors -mb-px',
              adminView === 'organizations'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Building2 className="h-4 w-4" />
            Organizations
          </button>
        </div>

        {adminView === 'users' ? (
          <>
        <form
          onSubmit={async (event) => {
            event.preventDefault()
            setInviteMessage(null)
            setGeneratedLink(null)
            try {
              await inviteUser.mutateAsync(inviteEmail)
              setInviteEmail('')
              setInviteMessage('Invitation sent with a personal trial license.')
            } catch (error) {
              setInviteMessage(error instanceof Error ? error.message : 'Failed to send invitation')
            }
          }}
          className="flex max-w-xl items-end gap-3 rounded-lg border border-white/[0.06] bg-white/[0.01] p-4"
        >
          <label className="flex-1 text-xs text-muted-foreground">
            User email
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="name@company.com"
              className="mt-2 w-full rounded-md border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-sm text-foreground"
            />
          </label>
          <button
            type="submit"
            disabled={anyLinkPending}
            title="Normal flow. Creates the account, grants a trial licence, and emails the setup link to the user."
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
          >
            {inviteUser.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Invite
          </button>
          <button
            type="button"
            disabled={anyLinkPending || !inviteEmail.trim()}
            title="Does NOT email anything. Creates the account and returns the setup link for you to deliver yourself. Use only when the user cannot receive our email."
            onClick={async () => {
              setInviteMessage(null)
              setGeneratedLink(null)
              setLinkCopied(false)
              try {
                const url = await generateInviteLink.mutateAsync(inviteEmail)
                setGeneratedLink(url)
                setInviteMessage('Link generated. No email was sent — send it to the user yourself.')
              } catch (error) {
                setInviteMessage(error instanceof Error ? error.message : 'Failed to generate link')
              }
            }}
            className="flex items-center gap-2 rounded-md border border-white/[0.08] px-3 py-2 text-sm text-muted-foreground/70 transition-colors hover:text-foreground disabled:opacity-40"
          >
            {generateInviteLink.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Generate link
          </button>
          <button
            type="button"
            disabled={anyLinkPending || !inviteEmail.trim()}
            title="For an account that already exists. Does NOT email anything and does not change the account — returns a password reset link for you to deliver yourself."
            onClick={async () => {
              setInviteMessage(null)
              setGeneratedLink(null)
              setLinkCopied(false)
              try {
                const url = await generateResetLink.mutateAsync(inviteEmail)
                setGeneratedLink(url)
                setInviteMessage('Reset link generated. No email was sent — send it to the user yourself.')
              } catch (error) {
                setInviteMessage(error instanceof Error ? error.message : 'Failed to generate reset link')
              }
            }}
            className="flex items-center gap-2 rounded-md border border-white/[0.08] px-3 py-2 text-sm text-muted-foreground/70 transition-colors hover:text-foreground disabled:opacity-40"
          >
            {generateResetLink.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Reset link
          </button>
        </form>
        {inviteMessage && <p className="text-xs text-muted-foreground">{inviteMessage}</p>}
        {generatedLink && (
          <div className="flex max-w-xl items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.01] p-3">
            {/* ph-no-capture: this is a working sign-in credential; keep it out of session replays. */}
            <code className="ph-no-capture flex-1 truncate text-xs text-muted-foreground">{generatedLink}</code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(generatedLink)
                setLinkCopied(true)
                setTimeout(() => setLinkCopied(false), 2000)
              }}
              className="flex items-center gap-1.5 rounded-md border border-white/[0.08] px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {linkCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}

        {/* Search Bar and Toolbar */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Search */}
          <div className="relative w-full max-w-md group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/40 transition-colors group-focus-within:text-muted-foreground/70" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, email, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-16 py-2.5 text-sm bg-white/[0.02] border border-white/[0.08] rounded-lg
                         text-foreground placeholder:text-muted-foreground/40
                         focus:outline-none focus:border-white/[0.15] focus:bg-white/[0.03] focus:shadow-[0_0_0_3px_rgba(255,255,255,0.03)]
                         transition-all duration-200"
            />
            {searchQuery ? (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground/40 hover:text-muted-foreground hover:bg-white/[0.04] transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/30 bg-white/[0.03] px-1.5 py-0.5 rounded border border-white/[0.06] font-mono">
                /
              </kbd>
            )}
          </div>

          {/* Filter Chips */}
          <div className="flex items-center gap-2">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilterOption(option.value)}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-full border transition-all duration-150',
                  filterOption === option.value
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-white/[0.02] border-white/[0.06] text-muted-foreground/60 hover:text-muted-foreground hover:border-white/[0.1] hover:bg-white/[0.03]'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error State */}
        {usersError && (
          <div className="text-center py-8">
            <p className="text-sm text-destructive/70">Failed to load users</p>
          </div>
        )}

        {/* Loading State - Skeleton */}
        {usersLoading && (
          <div className="border border-white/[0.06] rounded-lg overflow-hidden bg-white/[0.01]">
            {/* Skeleton Header */}
            <div className="bg-white/[0.02] border-b border-white/[0.06] py-3 px-4">
              <div className="flex gap-4">
                <div className="h-3 w-16 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-3 w-20 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-3 w-24 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-3 w-16 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-3 w-20 bg-white/[0.04] rounded animate-pulse" />
                <div className="h-3 w-12 bg-white/[0.04] rounded animate-pulse" />
              </div>
            </div>
            {/* Skeleton Rows */}
            <div className="divide-y divide-white/[0.04]">
              {[...Array(8)].map((_, i) => (
                <SkeletonRow key={i} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {!usersLoading && !usersError && (
          <div className="space-y-3">
            {/* Results count */}
            <p className="text-xs text-muted-foreground/50">
              {filteredAndSortedUsers.length} user
              {filteredAndSortedUsers.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
              {filterOption !== 'all' &&
                ` (${FILTER_OPTIONS.find((o) => o.value === filterOption)?.label.toLowerCase()})`}
            </p>

            {/* Table */}
            {filteredAndSortedUsers.length > 0 ? (
              <div className="border border-white/[0.06] rounded-lg overflow-x-auto overflow-y-hidden flex flex-col bg-white/[0.01]">
                {/* Fixed Header */}
                <div className="bg-white/[0.02] backdrop-blur-sm border-b border-white/[0.06] flex-shrink-0 min-w-[980px]">
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[150px]" />
                      <col className="w-[250px]" />
                      <col className="w-[130px]" />
                      <col className="w-[95px]" />
                      <col className="w-[170px]" />
                      <col className="w-[95px]" />
                      <col className="w-[90px]" />
                    </colgroup>
                    <thead>
                      <tr>
                        <SortableHeader
                          column="name"
                          label="Name"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="email"
                          label="Email"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="last_sign_in"
                          label="Last Sign In"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="devices"
                          label="Devices"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="quota"
                          label="Quota (km²)"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="role"
                          label="Role"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          column="beta"
                          label="Beta"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto max-h-[calc(100vh-380px)] min-h-[200px] min-w-[980px]">
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[150px]" />
                      <col className="w-[250px]" />
                      <col className="w-[130px]" />
                      <col className="w-[95px]" />
                      <col className="w-[170px]" />
                      <col className="w-[95px]" />
                      <col className="w-[90px]" />
                    </colgroup>
                    <tbody className="divide-y divide-white/[0.04]">
                      {paginatedUsers.map((user) => (
                        <UserTableRow
                          key={user.id}
                          user={user}
                          isSelected={selectedUserId === user.id}
                          onClick={() => handleSelectUser(user.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06] bg-white/[0.01]">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
                    <span>Showing</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value) as PageSize)}
                      className="bg-white/[0.03] border border-white/[0.08] rounded-md px-2 py-1 text-muted-foreground
                                 focus:outline-none focus:border-white/[0.15] transition-colors cursor-pointer"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span>of {totalUsers}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground/40">
                      Page {currentPage} of {totalPages}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState
                searchQuery={searchQuery}
                filterOption={filterOption}
                onClearFilters={() => {
                  setSearchQuery('')
                  setFilterOption('all')
                }}
              />
            )}
          </div>
        )}
          </>
        ) : (
          <OrganizationAdminView />
        )}
      </div>

      {/* Slide-over Panel */}
      {adminView === 'users' && panelState !== 'closed' && (
        <div
          className={cn(
            'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
            panelState === 'open' ? 'animate-fade-in' : 'animate-fade-out'
          )}
          onClick={closePanel}
        >
          <div
            className={cn(
              'absolute right-0 top-0 h-full w-full max-w-2xl bg-background border-l border-border/30 shadow-2xl overflow-y-auto',
              panelState === 'open' ? 'animate-slide-in-right' : 'animate-slide-out-right'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel Header */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border/20 px-6 py-4 flex items-center justify-between">
              <p className="text-xs text-muted-foreground/50">User details</p>
              <button
                onClick={closePanel}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Panel Content */}
            <div className="p-6">
              {selectedUser && (
                <UserDetailsPanel
                  user={selectedUser}
                  licenses={licenses}
                  devices={devices}
                  maps={maps}
                  licensesLoading={licensesLoading}
                  devicesLoading={devicesLoading}
                  mapsLoading={mapsLoading}
                  onToggleAdmin={(userId, isAdmin) => toggleAdmin.mutate({ userId, isAdmin })}
                  onToggleBetaTester={(userId, isBetaTester) => toggleBetaTester.mutate({ userId, isBetaTester })}
                  onUpdateLicense={(licenseId, updates) => updateLicense.mutate({ licenseId, updates })}
                  onCreateLicense={(license) => createLicense.mutate(license)}
                  onDeleteDevice={(deviceId) => deleteDevice.mutate(deviceId)}
                  deleteDeviceError={deleteDevice.error?.message ?? null}
                  onResetDeleteError={() => deleteDevice.reset()}
                  isTogglingAdmin={toggleAdmin.isPending}
                  isTogglingBetaTester={toggleBetaTester.isPending}
                  isUpdatingLicense={updateLicense.isPending}
                  isCreatingLicense={createLicense.isPending}
                  isDeletingDevice={deleteDevice.isPending}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

type SortableHeaderProps = {
  column: SortColumn
  label: string
  sortColumn: SortColumn
  sortDirection: SortDirection
  onSort: (column: SortColumn) => void
}

function SortableHeader({ column, label, sortColumn, sortDirection, onSort }: SortableHeaderProps) {
  const isActive = sortColumn === column
  const isNumeric = column === 'devices' || column === 'quota'

  return (
    <th
      onClick={() => onSort(column)}
      className={cn(
        'py-3 px-4 font-medium text-muted-foreground/70 text-[11px] uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none',
        isNumeric ? 'text-right' : 'text-left'
      )}
    >
      <div className={cn('flex items-center gap-1.5', isNumeric && 'justify-end')}>
        <span className="truncate">{label}</span>
        <div
          className={cn('transition-colors flex-shrink-0', isActive ? 'text-foreground' : 'text-muted-foreground/20')}
        >
          {isActive && sortDirection === 'desc' ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronUp className="h-3 w-3" />
          )}
        </div>
      </div>
    </th>
  )
}

type UserTableRowProps = {
  user: EnrichedUser
  isSelected: boolean
  onClick: () => void
}

function UserTableRow({ user, isSelected, onClick }: UserTableRowProps) {
  const [copied, setCopied] = useState(false)
  const license = user.active_license
  const deviceCount = user.device_count
  const totalDevices = license?.total_devices
  const usedQuota = user.used_quota_km2
  const totalQuota = license?.annual_quota_km2
  const hasLicense = !!license
  const hasMvps = license?.license_tiers.includes('LIC-MVPS')
  const onlyMvps = hasMvps && license?.license_tiers.length === 1

  const handleCopyEmail = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (user.email) {
      navigator.clipboard.writeText(user.email)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <tr
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      className={cn(
        'cursor-pointer transition-colors duration-100 group outline-none',
        'focus-visible:bg-white/[0.03] focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary/30',
        isSelected ? 'bg-primary/10 hover:bg-primary/[0.12]' : 'hover:bg-white/[0.02]'
      )}
    >
      <td className="py-3.5 px-4">
        {user.full_name ? (
          <span className="text-sm text-foreground truncate block">{user.full_name}</span>
        ) : (
          <span className="text-sm text-muted-foreground/40 italic">No name</span>
        )}
      </td>
      <td className="py-3.5 px-4">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-muted-foreground truncate flex-1" title={user.email}>
            {user.email || '—'}
          </span>
          {user.email && (
            <button
              onClick={handleCopyEmail}
              className="flex-shrink-0 p-1 rounded-md text-muted-foreground/30 hover:text-muted-foreground hover:bg-white/[0.04] opacity-0 group-hover:opacity-100 transition-all"
              title="Copy email"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </td>
      <td className="py-3.5 px-4">
        <span className="text-sm text-muted-foreground/70 truncate block">
          {formatLastSeen(user.last_sign_in_at ?? null)}
        </span>
      </td>
      <td className="py-3.5 px-4 text-right tabular-nums">
        {hasLicense ? (
          onlyMvps ? (
            <span className="text-sm text-muted-foreground/50">n/a</span>
          ) : (
            <>
              <span className="text-sm text-foreground">{deviceCount}</span>
              <span className="text-sm text-muted-foreground/40">/{totalDevices}</span>
            </>
          )
        ) : (
          <span className="text-sm text-muted-foreground/30">—</span>
        )}
        {license && (
          <span className="mt-1 block text-[11px] text-muted-foreground/50">
            {license.license_tiers.join(', ')}
          </span>
        )}
      </td>
      <td className="py-3.5 px-4 text-right tabular-nums">
        {hasLicense ? (
          hasMvps ? (
            <span className="text-sm text-muted-foreground/50">Unlimited</span>
          ) : (
            <>
              <span className="text-sm text-foreground">{usedQuota.toFixed(1)}</span>
              <span className="text-sm text-muted-foreground/40">/{totalQuota}</span>
            </>
          )
        ) : (
          <span className="text-sm text-muted-foreground/30">—</span>
        )}
      </td>
      <td className="py-3.5 px-4">
        {user.is_admin ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-primary bg-primary/10 px-2 py-1 rounded-full shadow-[0_0_12px_rgba(99,215,6,0.1)]">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Admin
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/50 bg-white/[0.03] px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
            User
          </span>
        )}
      </td>
      <td className="py-3.5 px-4">
        {user.is_beta_tester ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-sky-300 bg-sky-500/10 px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-300" />
            Beta
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/50 bg-white/[0.03] px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
            Main
          </span>
        )}
      </td>
    </tr>
  )
}

function SkeletonRow({ index }: { index: number }) {
  return (
    <div className="flex items-center py-3.5 px-4 gap-4 animate-pulse" style={{ animationDelay: `${index * 50}ms` }}>
      {/* Name */}
      <div className="w-[14%]">
        <div className="h-4 w-24 bg-white/[0.04] rounded" />
      </div>
      {/* Email */}
      <div className="w-[24%]">
        <div className="h-4 w-40 bg-white/[0.04] rounded" />
      </div>
      {/* Last Sign In */}
      <div className="w-[14%]">
        <div className="h-4 w-20 bg-white/[0.04] rounded" />
      </div>
      {/* Devices */}
      <div className="w-[10%] flex justify-end">
        <div className="h-4 w-10 bg-white/[0.04] rounded" />
      </div>
      {/* Quota */}
      <div className="w-[15%] flex justify-end">
        <div className="h-4 w-16 bg-white/[0.04] rounded" />
      </div>
      {/* Role */}
      <div className="w-[11%]">
        <div className="h-5 w-14 bg-white/[0.04] rounded-full" />
      </div>
      {/* Beta */}
      <div className="w-[12%]">
        <div className="h-5 w-14 bg-white/[0.04] rounded-full" />
      </div>
    </div>
  )
}

type EmptyStateProps = {
  searchQuery: string
  filterOption: FilterOption
  onClearFilters: () => void
}

function EmptyState({ searchQuery, filterOption, onClearFilters }: EmptyStateProps) {
  const hasFilters = searchQuery || filterOption !== 'all'

  return (
    <div className="py-16 text-center">
      <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
        <Users className="w-6 h-6 text-muted-foreground/40" />
      </div>
      <p className="text-sm text-muted-foreground">
        {hasFilters ? 'No users match your search' : 'No users available'}
      </p>
      <p className="text-xs text-muted-foreground/50 mt-1.5">
        {hasFilters ? 'Try adjusting your search or filters' : 'Users will appear here once they are invited'}
      </p>
      {hasFilters && (
        <button onClick={onClearFilters} className="mt-5 text-xs text-primary hover:text-primary/80 transition-colors">
          Clear all filters
        </button>
      )}
    </div>
  )
}
